import { useCallback } from "react";

import useAvailableLogins from "shared/User/useAvailableLogins";
import TextEllipsis from "ds/components/TextEllipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownSubmenu from "ds/components/DropdownMenu/Submenu";
import Icon from "ds/components/Icon";
import { ChevronRight } from "components/icons/generated";
import SpaceliftLogo from "ds/components/SpaceliftLogo/Logo";
import { getRegionPartOfDomain, REGIONS_DISPLAY_NAMES } from "utils/region";
import Typography from "ds/components/Typography";
import Box from "ds/components/Box";

import { showLoginListModal } from "./Modal";
import { extractAppURL } from "./utils";
import styles from "./styles.module.css";

type MainNavigationLoginListProps = {
  accountName: string;
  onCloseDropdown: () => void;
};

const MainNavigationLoginList = ({
  accountName,
  onCloseDropdown,
}: MainNavigationLoginListProps) => {
  const { logins, hasMultipleRegions } = useAvailableLogins({ accountName });
  const appURL = extractAppURL(window.location.hostname);

  const handleModalShow = useCallback(() => {
    onCloseDropdown();
    showLoginListModal({ logins });
  }, [onCloseDropdown, logins]);

  return (
    <DropdownSubmenu
      className={styles.menu}
      placement="right top"
      triggerComponent={
        <DropdownMenuItem className={styles.trigger}>
          Change account
          <Icon src={ChevronRight} />
        </DropdownMenuItem>
      }
    >
      {logins.map(({ accountName, region }) => {
        const regionPartOfDomain = getRegionPartOfDomain(region);
        const login = regionPartOfDomain ? `${accountName}.${regionPartOfDomain}` : accountName;

        return (
          <DropdownMenuItem key={`login-${login}`} href={`https://${login}.${appURL}`}>
            <Box align="center" justify="between" gap="small" fullWidth>
              <Box align="center">
                <SpaceliftLogo className={styles.listItemLogo} size="xSmall" />

                <TextEllipsis tooltip={accountName}>
                  {(props) => <span {...props}>{accountName}</span>}
                </TextEllipsis>
              </Box>
              {hasMultipleRegions && (
                <Typography variant="p-body3" color="secondary" tag="span">
                  {REGIONS_DISPLAY_NAMES[region]}
                </Typography>
              )}
            </Box>
          </DropdownMenuItem>
        );
      })}

      <DropdownMenuItem onAction={handleModalShow} className={styles.addAccountItem}>
        + Add account
      </DropdownMenuItem>
    </DropdownSubmenu>
  );
};

export default MainNavigationLoginList;
